import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Re-fetch a set of queries and resolve only once they've returned
 * fresh data. Use after any mutation that should be reflected in the
 * UI immediately.
 *
 * Why this exists: `queryClient.invalidateQueries` only refetches
 * queries that have an active observer. Our routes read data via
 * `ensureQueryData` in `beforeLoad`, which does NOT subscribe — so
 * invalidating only marks the cache stale, and the next
 * `ensureQueryData` returns the same stale data (with a background
 * refetch the route has already missed). The result: a delete UI
 * fires its toast but the list keeps the deleted row on screen until
 * a hard reload.
 *
 * `refetchQueries` always triggers a fetch AND awaits its completion,
 * so the cache is fresh before we re-run the route's loader via
 * `router.invalidate()`.
 *
 * Project guideline (the CommitMind mutation-refresh rule ("Mutation refresh pattern"): after a
 * mutation, call `refreshQueries(queryClient, ...keys)` and THEN
 * `await router.invalidate()`. Never use `invalidateQueries` alone in
 * a mutation handler.
 */
export async function refreshQueries(queryClient: QueryClient, ...keys: QueryKey[]): Promise<void> {
  await Promise.all(keys.map((key) => queryClient.refetchQueries({ queryKey: key })));
}
